import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';
import { PartnersTab, TransfersTab } from './partners';

type Side = 'customer' | 'supplier';
interface Party { side: Side; key: string; name: string; code: string; email: string; balance: number; documents: number; maxDaysOverdue: number; aging: { notDue: number; d30: number; d60: number; d90: number; over90: number; other: number } }
interface ActRow { date: string; document: string; debit: number; credit: number }
interface Act { party: Party; from: string; to: string; opening: number; rows: ActRow[]; debit: number; credit: number; closing: number }
interface SentAct { id: number; number: string; side: Side; partyKey: string; partyName: string; email: string; periodFrom: string; periodTo: string; closing: number; status: 'sent' | 'confirmed' | 'disputed'; responderName: string | null; responseNote: string | null; respondedAt: string | null; mailStatus: 'sent' | 'failed'; createdAt: string }
interface Project { id: number; name: string; smtpHost: string | null; emailFrom: string | null; hasSmtpPassword: boolean }

const today = () => new Date().toLocaleDateString('sv-SE');
const STATUS: Record<SentAct['status'], string> = { sent: 'Laukiama atsakymo', confirmed: 'Patvirtinta', disputed: 'Nesutinka' };

export type ReconciliationTab = 'partners' | 'transfers' | 'ledger' | 'acts';
type Tab = ReconciliationTab;
// The menu's tabs (Partneriai and Pinigai) choose the list; the title follows.
const TITLES: Record<Tab, [string, string, string]> = {
  partners: ['Partneriai', 'Balansai', 'Su kuo kiek atsiskaityta: sąskaitos, mokėjimai, likutis ir kas trūksta.'],
  ledger: ['Partneriai', 'Skolų žiniaraštis', 'Kas kam skolingas pasirinktą dieną, pagal vėlavimą.'],
  acts: ['Partneriai', 'Suderinimo aktai', 'Aktai partneriams ir jų atsakymai.'],
  transfers: ['Pinigai', 'Vidiniai pervedimai', 'Pervedimai tarp savo sąskaitų ir susijusių įmonių.'],
};

/**
 * Atsiskaitymai: partners (everyone, with documents, money, balance and what is missing), transfers between
 * own accounts and related companies, the debt list at a date and the reconciliation acts.
 */
export function ReconciliationPage({ me, tab, onTab }: { me: Me; tab: Tab; onTab: (tab: Tab) => void }) {
  const { t } = useI18n();
  const setTab = onTab;
  const [date, setDate] = useState(today());
  const [chosen, setChosen] = useState<Party | null>(null);
  const parties = useQuery({ queryKey: ['reconciliation', date], queryFn: () => api.get<{ parties: Party[] }>(`api/v1/reconciliation/parties?date=${date}`), enabled: tab === 'ledger' });
  const acts = useQuery({ queryKey: ['reconciliation-acts'], queryFn: () => api.get<{ acts: SentAct[] }>('api/v1/reconciliation/acts'), enabled: tab === 'acts' });
  const list = parties.data?.parties ?? [];
  // From a partner card: the act panel for that customer or supplier.
  const actFor = (p: { key: string; name: string; side: Side }) => {
    setChosen({ side: p.side, key: p.key, name: p.name, code: '', email: '', balance: 0, documents: 0, maxDaysOverdue: 0, aging: { notDue: 0, d30: 0, d60: 0, d90: 0, over90: 0, other: 0 } });
    setTab('acts');
  };
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t(TITLES[tab][0])} title={t(TITLES[tab][1])} text={t(TITLES[tab][2])} />}>
      {tab === 'partners' ? <PartnersTab us={me.company.name} onAct={actFor} /> : null}
      {tab === 'transfers' ? <TransfersTab /> : null}
      {tab === 'ledger' ? <>
        <div className="recon-head">
          <label><span>{t('Data')}</span><input type="date" value={date} max={today()} onChange={(e) => e.target.value && setDate(e.target.value)} /></label>
          <a className="button button--ghost button--small" href={`api/v1/reconciliation/ledger.pdf?date=${date}`}>{t('Skolų žiniaraštis')} PDF</a>
          <a className="button button--ghost button--small" href={`api/v1/reconciliation/ledger.csv?date=${date}`}>CSV</a>
        </div>
        {parties.isError ? <Alert kind="error">{errorText(t, parties.error)}</Alert> : null}
        {chosen ? <ActPanel party={chosen} to={date} onClose={() => setChosen(null)} /> : null}
        <Section title={t('Debitoriai — mums skolingi')} rows={list.filter((p) => p.side === 'customer')} onAct={setChosen} />
        <Section title={t('Kreditoriai — mes skolingi')} rows={list.filter((p) => p.side === 'supplier')} onAct={setChosen} />
      </> : null}
      {tab === 'acts' ? <>
        {chosen ? <ActPanel party={chosen} to={date} onClose={() => setChosen(null)} /> : <p className="muted">{t('Aktą sudarykite iš partnerio kortelės arba skolų žiniaraščio.')}</p>}
        <SentActs acts={acts.data?.acts ?? []} />
      </> : null}
    </PanelShell>
  );
}

function Section({ title, rows, onAct }: { title: string; rows: Party[]; onAct: (p: Party) => void }) {
  const { t } = useI18n();
  const sum = (f: (p: Party) => number) => rows.reduce((s, p) => s + f(p), 0);
  return (
    <section className="panel-card">
      <h2>{title} · {money(sum((p) => p.balance))}</h2>
      {rows.length ? (
        <div className="table-wrap">
          <table className="invoice-table recon-table">
            <thead><tr><th>{t('Pavadinimas')}</th><th className="num">{t('Dok.')}</th><th className="num">{t('Nevėluoja')}</th><th className="num">1–30 d.</th><th className="num">31–60 d.</th><th className="num">61–90 d.</th><th className="num">&gt;90 d.</th><th className="num">{t('Kita')}</th><th className="num">{t('Iš viso')}</th><th /></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.key}>
                  <td><strong>{p.name}</strong>{p.code ? <small>{p.code}</small> : null}</td>
                  <td className="num">{p.documents}</td>
                  {[p.aging.notDue, p.aging.d30, p.aging.d60, p.aging.d90, p.aging.over90, p.aging.other].map((v, i) => <td key={i} className={`num${i >= 2 && i <= 4 && v ? ' recon-late' : ''}`}>{v ? money(v) : ''}</td>)}
                  <td className="num"><strong>{money(p.balance)}</strong></td>
                  <td><button type="button" className="button button--ghost button--small" onClick={() => onAct(p)}>{t('Suderinimo aktas')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="muted">{t('Nėra.')}</p>}
    </section>
  );
}

function ActPanel({ party, to: initialTo, onClose }: { party: Party; to: string; onClose: () => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [from, setFrom] = useState(`${initialTo.slice(0, 4)}-01-01`);
  const [to, setTo] = useState(initialTo);
  const [email, setEmail] = useState(party.email);
  const [projectId, setProjectId] = useState('');
  const [message, setMessage] = useState('');
  const query = `side=${party.side}&key=${encodeURIComponent(party.key)}&from=${from}&to=${to}`;
  const act = useQuery({ queryKey: ['reconciliation-act', query], queryFn: () => api.get<{ act: Act; result: string }>(`api/v1/reconciliation/act?${query}`), enabled: from <= to });
  const projects = useQuery({ queryKey: ['projects', 'response'], queryFn: () => api.get<{ projects: Project[] }>('api/v1/projects') });
  const mailable = (projects.data?.projects ?? []).filter((p) => p.smtpHost && p.emailFrom && p.hasSmtpPassword);
  const send = useMutation({
    mutationFn: () => api.post<{ act: { number: string; mailStatus: 'sent' | 'failed' } }>('api/v1/reconciliation/acts', {
      side: party.side, key: party.key, from, to, email, ...(projectId ? { projectId: Number(projectId) } : {}), ...(message.trim() ? { message: message.trim() } : {}),
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reconciliation-acts'] }),
  });
  const submit = (e: FormEvent) => { e.preventDefault(); send.mutate(); };
  const a = act.data?.act;
  return (
    <section className="panel-card recon-act">
      <div className="card-head">
        <div><p className="eyebrow">{party.side === 'customer' ? t('Pirkėjas') : t('Tiekėjas')}</p><h2>{t('Suderinimo aktas')}: {party.name}</h2></div>
        <button type="button" className="text-link" onClick={onClose}>{t('Uždaryti')}</button>
      </div>
      <div className="recon-period">
        <label><span>{t('Nuo')}</span><input type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>
        <label><span>{t('Iki')}</span><input type="date" value={to} onChange={(e) => e.target.value && setTo(e.target.value)} /></label>
        <a className="button button--ghost button--small" href={`api/v1/reconciliation/act.pdf?${query}`}>PDF</a>
      </div>
      {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}
      {a ? (
        <div className="table-wrap">
          <table className="invoice-table">
            <thead><tr><th>{t('Data')}</th><th>{t('Dokumentas')}</th><th className="num">{t('Debetas')}</th><th className="num">{t('Kreditas')}</th></tr></thead>
            <tbody>
              <tr className="recon-sum"><td>{a.from}</td><td>{t('Likutis laikotarpio pradžioje')}</td><td className="num" colSpan={2}>{money(a.opening)}</td></tr>
              {a.rows.map((r, i) => <tr key={i}><td>{r.date}</td><td>{r.document}</td><td className="num">{r.debit ? money(r.debit) : ''}</td><td className="num">{r.credit ? money(r.credit) : ''}</td></tr>)}
              <tr className="recon-sum"><td /><td>{t('Apyvarta')}</td><td className="num">{money(a.debit)}</td><td className="num">{money(a.credit)}</td></tr>
              <tr className="recon-sum"><td>{a.to}</td><td>{t('Likutis laikotarpio pabaigoje')}</td><td className="num" colSpan={2}>{money(a.closing)}</td></tr>
            </tbody>
          </table>
        </div>
      ) : null}
      {act.data ? <p className="recon-result">{act.data.result}</p> : null}

      <form className="recon-send" onSubmit={submit}>
        <h3>{t('Siųsti el. paštu')}</h3>
        <div className="form-grid">
          <label><span>{t('Gavėjo el. paštas')}</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
          <label><span>{t('Siųsti iš')}</span>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              {mailable.map((p, i) => <option key={p.id} value={i === 0 ? '' : p.id}>{p.name} · {p.emailFrom}</option>)}
            </select>
          </label>
        </div>
        <label><span>{t('Laiško pradžia (nebūtina)')}</span><textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={3000} /></label>
        {!mailable.length && projects.isSuccess ? <Alert kind="error">{t('Nėra projekto su el. pašto nustatymais. Nustatykite SMTP skiltyje Projektai arba atsisiųskite PDF ir išsiųskite patys.')}</Alert> : null}
        {send.isError ? <Alert kind="error">{errorText(t, send.error)}</Alert> : null}
        {send.isSuccess ? <Alert kind={send.data.act.mailStatus === 'sent' ? 'success' : 'error'}>{send.data.act.mailStatus === 'sent' ? t('Aktas {number} išsiųstas. Atsakymą matysite žemiau.', { number: send.data.act.number }) : t('Aktas {number} išsaugotas, bet laiško išsiųsti nepavyko. Patikrinkite projekto SMTP.', { number: send.data.act.number })}</Alert> : null}
        <div className="form-actions"><button type="submit" className="button button--primary" disabled={send.isPending || !mailable.length || !a}>{t('Siųsti aktą')}</button></div>
        <small className="muted">{t('Laiške bus PDF ir nuoroda, kurioje gavėjas patvirtina likutį arba nurodo nesutapimus.')}</small>
      </form>
    </section>
  );
}

function SentActs({ acts }: { acts: SentAct[] }) {
  const { t } = useI18n();
  return (
    <section className="panel-card">
      <h2>{t('Išsiųsti aktai')}</h2>
      {acts.length ? (
        <div className="table-wrap">
          <table className="invoice-table">
            <thead><tr><th>Nr.</th><th>{t('Kam')}</th><th>{t('Dienai')}</th><th className="num">{t('Likutis')}</th><th>{t('Būsena')}</th><th /></tr></thead>
            <tbody>
              {acts.map((a) => (
                <tr key={a.id}>
                  <td>{a.number}</td>
                  <td><strong>{a.partyName}</strong><small>{a.email}</small></td>
                  <td>{a.periodTo}<small>{a.periodFrom} – {a.periodTo}</small></td>
                  <td className="num">{money(a.closing)}</td>
                  <td>
                    <span className={`badge badge--${a.status === 'confirmed' ? 'paid' : a.status === 'disputed' ? 'overdue' : 'sent'}`}>{t(STATUS[a.status])}</span>
                    {a.mailStatus === 'failed' ? <small className="recon-late">{t('Laiškas neišsiųstas')}</small> : null}
                    {a.responderName ? <small>{a.responderName}{a.respondedAt ? ` · ${new Date(a.respondedAt).toLocaleDateString()}` : ''}</small> : null}
                    {a.responseNote ? <small>„{a.responseNote}“</small> : null}
                  </td>
                  <td><a className="text-link" href={`api/v1/reconciliation/acts/${a.id}/pdf`}>PDF</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="muted">{t('Aktų dar nesiuntėte.')}</p>}
    </section>
  );
}
